import { ArrowUp, Check, Lock, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { layoutSkillNodes, skillDependencyPath } from "../skillLayout";
import { type GrowthProfile, type GrowthSkill } from "../types";
import { AttackElementMark } from "../ui/Elements";
import { ResourceIcon } from "../ui/Resources";
import {
  MANA_TIER_LABELS,
  SKILL_BRANCHES,
  SKILL_BRANCH_LABELS,
  SkillBranchIcon,
  SkillIcon,
  formatCooldownMs,
  manaTier,
  skillResourceLimitText,
} from "../ui/SkillControls";

export function SkillsScreen({
  growth,
  busy,
  onSave,
  onUnlock,
  onRespec,
}: {
  growth: GrowthProfile;
  busy: boolean;
  onSave: (skills: string[]) => void;
  onUnlock: (skillId: string) => void;
  onRespec: () => void;
}) {
  const [activeSlot, setActiveSlot] = useState(0);
  const [activeBranch, setActiveBranch] = useState<GrowthSkill["branch"]>("resource");
  const [selected, setSelected] = useState(() => [...growth.skillSlots]);
  useEffect(() => setSelected([...growth.skillSlots]), [growth.skillSlots]);
  const chooseSkill = (skillId: string) => {
    const existingSlot = selected.indexOf(skillId);
    setSelected((current) => {
      const next = [...current];
      if (existingSlot >= 0) {
        [next[activeSlot], next[existingSlot]] = [next[existingSlot], next[activeSlot]];
      } else {
        next[activeSlot] = skillId;
      }
      return next;
    });
  };
  const changed = selected.join(":") !== growth.skillSlots.join(":");
  const activeSkills = growth.skills
    .filter((skill) => skill.branch === activeBranch)
    .sort((left, right) => left.tier - right.tier || left.column - right.column);
  const skillLayout = useMemo(() => layoutSkillNodes(activeSkills), [growth.skills, activeBranch]);
  const [inspectedSkill, setInspectedSkill] = useState("");
  useEffect(() => setInspectedSkill(""), [activeBranch]);
  const activeMastery = growth.masteries?.find((entry) => entry.branch === activeBranch);
  const masteryProgress = activeMastery?.nextLevelExperience
    ? (activeMastery.levelExperience / activeMastery.nextLevelExperience) * 100
    : 100;
  const changeBranchByKey = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    branch: GrowthSkill["branch"],
  ) => {
    const current = SKILL_BRANCHES.indexOf(branch);
    let next = current;
    if (event.key === "ArrowRight") next = (current + 1) % SKILL_BRANCHES.length;
    else if (event.key === "ArrowLeft")
      next = (current - 1 + SKILL_BRANCHES.length) % SKILL_BRANCHES.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = SKILL_BRANCHES.length - 1;
    else return;
    event.preventDefault();
    const nextBranch = SKILL_BRANCHES[next];
    setActiveBranch(nextBranch);
    event.currentTarget.parentElement
      ?.querySelector<HTMLButtonElement>(`#skill-tab-${nextBranch}`)
      ?.focus();
  };
  return (
    <section className="growth-dialog growth-screen" aria-labelledby="skills-title">
      <header className="growth-dialog-header">
        <div>
          <h1 id="skills-title">技能树</h1>
          <small className="skill-points-readout">
            可用技能点 {growth.skillPoints} · 已用 {growth.skillPointsSpent}
          </small>
        </div>
        <span>人物 Lv.{growth.characterLevel}</span>
      </header>
      <div className="skill-slots" aria-label="战斗技能配置">
        {selected.map((skillId, slot) => {
          const skill = growth.skills.find((candidate) => candidate.id === skillId);
          return (
            <button
              key={slot}
              className={activeSlot === slot ? "is-active" : ""}
              aria-label={`技能槽 ${slot + 1}：${skill?.name ?? skillId}`}
              onClick={() => setActiveSlot(slot)}
            >
              <b>{slot + 1}</b>
              <span>
                <SkillIcon skillId={skillId} size={18} />
                {skill?.name ?? skillId}
                <small>R{skill?.rank ?? 1}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="skill-branch-tabs" role="tablist" aria-label="技能方向">
        {SKILL_BRANCHES.map((branch) => {
          const mastery = growth.masteries?.find((entry) => entry.branch === branch);
          return (
            <button
              key={branch}
              id={`skill-tab-${branch}`}
              className={`skill-branch-tab branch-${branch}${activeBranch === branch ? " is-active" : ""}`}
              type="button"
              role="tab"
              aria-selected={activeBranch === branch}
              aria-controls="skill-branch-panel"
              tabIndex={activeBranch === branch ? 0 : -1}
              onClick={() => setActiveBranch(branch)}
              onKeyDown={(event) => changeBranchByKey(event, branch)}
            >
              <SkillBranchIcon branch={branch} />
              <span>
                <strong>{SKILL_BRANCH_LABELS[branch]}</strong>
                <small>Lv.{mastery?.level ?? 1}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div
        className={`skill-tree branch-${activeBranch}`}
        id="skill-branch-panel"
        role="tabpanel"
        aria-labelledby={`skill-tab-${activeBranch}`}
      >
        <section
          className={`skill-branch branch-${activeBranch}`}
          aria-label={`${SKILL_BRANCH_LABELS[activeBranch]}技能树`}
        >
          <header
            title={`${SKILL_BRANCH_LABELS[activeBranch]}熟练度 ${activeMastery?.experience ?? 0}`}
          >
            <SkillBranchIcon branch={activeBranch} />
            <div>
              <strong>{SKILL_BRANCH_LABELS[activeBranch]}</strong>
              <i>
                <em style={{ width: `${masteryProgress}%` }} />
              </i>
            </div>
            <span>
              {growth.treePoints?.[activeBranch] ?? 0} 点 · Lv.{activeMastery?.level ?? 1}
            </span>
          </header>
          <div
            className={`skill-branch-nodes${inspectedSkill ? " is-inspecting" : ""}`}
            onMouseLeave={() => setInspectedSkill("")}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setInspectedSkill("");
            }}
          >
            <svg
              className="skill-tree-links"
              viewBox="0 0 500 800"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {activeSkills.flatMap((skill, edgeIndex) =>
                (skill.prerequisites ?? []).map((prerequisite) => {
                  const parent = activeSkills.find((candidate) => candidate.id === prerequisite);
                  if (!parent) return null;
                  const parentLayout = skillLayout.get(parent.id) ?? {
                    row: parent.tier,
                    col: parent.column,
                  };
                  const childLayout = skillLayout.get(skill.id) ?? {
                    row: skill.tier,
                    col: skill.column,
                  };
                  const state =
                    parent.unlocked && skill.unlocked
                      ? " is-unlocked"
                      : skill.canUnlock
                        ? " can-unlock"
                        : "";
                  const path = skillDependencyPath(parentLayout, childLayout, edgeIndex);
                  const connected = inspectedSkill === skill.id || inspectedSkill === prerequisite;
                  const endX = (childLayout.col - 0.5) * 100,
                    endY = (childLayout.row - 0.5) * 100 - 38;
                  return (
                    <g
                      key={`${prerequisite}-${skill.id}`}
                      className={`skill-tree-link${state}${connected ? " is-inspected" : ""}`}
                    >
                      <path className="skill-link-backdrop" d={path} />
                      <path className="skill-link-stroke" d={path} />
                      <path
                        className="skill-link-stroke"
                        d={`M ${endX - 3} ${endY - 4} L ${endX} ${endY} L ${endX + 3} ${endY - 4}`}
                      />
                    </g>
                  );
                }),
              )}
            </svg>
            {activeSkills.map((skill) => {
              const prerequisiteNames = (skill.prerequisites ?? [])
                .map((id) => growth.skills.find((candidate) => candidate.id === id)?.name ?? id)
                .join("、");
              const active = skill.kind === "active";
              const nodeLayout = skillLayout.get(skill.id) ?? {
                row: skill.tier,
                col: skill.column,
              };
              const tier = manaTier(skill);
              return (
                <article
                  key={skill.id}
                  onMouseEnter={() => setInspectedSkill(skill.id)}
                  onFocusCapture={() => setInspectedSkill(skill.id)}
                  style={{ gridRow: nodeLayout.row, gridColumn: nodeLayout.col }}
                  className={`skill-node is-column-${nodeLayout.col}${selected.includes(skill.id) ? " is-equipped" : ""}${skill.unlocked ? "" : " is-locked"}${skill.canUnlock ? " can-unlock" : ""}`}
                >
                  <button
                    type="button"
                    className="skill-node-main"
                    aria-label={`${skill.name}，${skill.description}`}
                    aria-pressed={active ? selected.includes(skill.id) : undefined}
                    disabled={busy || !active || !skill.unlocked}
                    onClick={() => active && skill.unlocked && chooseSkill(skill.id)}
                  >
                    <span className="skill-library-icon">
                      <SkillIcon skillId={skill.id} size={21} />
                    </span>
                    {!skill.unlocked && (
                      <span className="skill-lock-mark" aria-hidden="true">
                        <Lock size={10} />
                      </span>
                    )}
                    <span className="skill-node-copy">
                      <strong>{skill.name}</strong>
                      <small>
                        {active
                          ? `${MANA_TIER_LABELS[tier] ?? "技能"} ${skill.manaCost} · 冷却 ${formatCooldownMs(skill.cooldownMs)}`
                          : "被动"}
                      </small>
                      {skill.damageElement && <AttackElementMark element={skill.damageElement} />}
                    </span>
                  </button>
                  <span className="skill-point-rank">
                    {skill.points}/{skill.maxPoints}
                  </span>
                  {skill.points < skill.maxPoints && (
                    <button
                      type="button"
                      className="skill-invest"
                      title={`投入 1 技能点到${skill.name}`}
                      aria-label={`投入 1 技能点到${skill.name}`}
                      disabled={busy || !skill.canUnlock}
                      onClick={() => onUnlock(skill.id)}
                    >
                      <ArrowUp size={13} />
                    </button>
                  )}
                  <span className="skill-tooltip" role="tooltip">
                    <span>{skill.description}</span>
                    {active && (
                      <small className="skill-tooltip-timing">
                        {MANA_TIER_LABELS[tier] ?? "技能"}法力 {skill.manaCost} · 冷却时间{" "}
                        {formatCooldownMs(skill.cooldownMs)}
                        {skillResourceLimitText(skill, skill.resourceUseLimit)}
                      </small>
                    )}
                    <small>
                      {skill.points > 0
                        ? `${skill.kind === "active" ? `R${skill.rank}/${skill.maxRank} · ` : ""}节点 ${skill.points}/${skill.maxPoints} · 当前效果 ${Math.round(skill.pointPower * 100)}%`
                        : `需要 ${skill.unlock}${prerequisiteNames ? ` · 前置：${prerequisiteNames}` : ""}`}
                    </small>
                  </span>
                </article>
              );
            })}
          </div>
        </section>
      </div>
      <footer className="growth-dialog-footer">
        <button
          className="secondary-command compact"
          title={`消耗 ${growth.respecCrystalCost} 结晶洗点`}
          disabled={
            busy ||
            growth.skillPointsSpent === 0 ||
            growth.balances.crystal < growth.respecCrystalCost
          }
          onClick={() =>
            window.confirm(`消耗 ${growth.respecCrystalCost} 结晶重置全部额外技能点？`) &&
            onRespec()
          }
        >
          <RefreshCw size={17} />
          洗点 · <ResourceIcon kind="crystal" size={14} />
          {growth.respecCrystalCost}
        </button>
        <button
          className="primary-command compact"
          disabled={busy || !changed || selected.length !== 3}
          onClick={() => onSave(selected)}
        >
          <Check size={18} />
          应用配置
        </button>
      </footer>
    </section>
  );
}
